import * as angular from "angular";
import {ScheduleType} from "../types/schedule.type";

export class ScheduleRequestsService {

  constructor(private ScheduleAPI, private ScheduleTemplateAPI, private $schedule, private $scheduleTemplate, private splitService, private httpService) {
  }

  getAll(from, to, agentType, branchIds, returnInstances) {
    const api = this.ScheduleAPI.actions.getManyAgents;
    return this.$$call(api, {
      from: from,
      to: to,
      agentType: agentType,
      branchIds: branchIds,
      returnInstances: returnInstances
    }, {}, {});
  }

  getByAgentId(agentId: string, from: string, to: string, scheduleTypes?: ScheduleType[]) {
    const api = this.ScheduleAPI.actions.getByAgentId;
    return this.$$call(api, {
      from: from,
      to: to,
      scheduleType: scheduleTypes,
      zoneId: this.splitService.getTimeZone()
    }, {}, {id: agentId});
  }

  getByAgentIds(agentIds, from, to, scheduleTypes?, serviceAreaIds?, excludeScheduleTemplate?) {
    const api = this.ScheduleAPI.actions.getManyAgents;
    return this.$$call(api, {
      agentIds: agentIds,
      from: from,
      to: to,
      scheduleType: scheduleTypes,
      serviceAreaIds: serviceAreaIds,
      excludeScheduleTemplate: excludeScheduleTemplate
    }, {}, {});
  }

  getManyByIds(ids: string[], agentId: string) {
    const api = this.ScheduleAPI.actions.getManyByIds;
    return this.$$call(api, {
      scheduleIds: ids
    }, {}, {});
  }

  getAgentScheduleInstances(from, to, agentType, branchIds, excludeScheduleTemplate) {
    const api = this.ScheduleAPI.actions.getAgentScheduleInstances;
    return this.$$call(api, {
      from: from,
      to: to,
      agentType: agentType,
      branchIds: branchIds,
      excludeScheduleTemplate: excludeScheduleTemplate
    }, {}, {});
  }

  getAgentScheduleInstancesByAgentId(from, to, branchId?, agentsIdsList?) {
    const api = this.ScheduleAPI.actions.getAgentScheduleInstances;
    return this.$$call(api, {from: from, to: to, branchId: branchId, agentIds: agentsIdsList}, {}, {});
  }

  createManyByAgentId(schedules, agentId) {
    const api = this.ScheduleAPI.actions.createManyByAgentId;
    return this.$$call(api, {}, schedules, {id: agentId});
  }

  createLeaveByAgentId(leave, agentId) {
    const api = this.ScheduleAPI.actions.createLeaveByAgentId;
    return this.$$call(api, {}, leave, {id: agentId});
  }

  // Uses httpService directly (bypassing $$call/bringoz-object) so the raw HTTP status
  // survives on rejection - $$call's underlying request() collapses errors to error.data,
  // and the real validate error body carries no status field of its own to fall back on.
  validateManyByAgentId(schedules, agentId) {
    const api = this.ScheduleAPI.actions.validateManyByAgentId;
    const url = this.ScheduleAPI.base + '/' + api.address.replace('{id}', encodeURIComponent(agentId));
    return this.httpService.request(url, {
      method: api.cmd,
      data: schedules,
      params: {siteId: this.splitService.getSiteId()}
    });
  }

  autoCreate(from, to, holderId: string, holderType: string) {
    const api = this.ScheduleAPI.actions.autoCreate;
    return this.$$call(api, {from: from, to: to, holderId: holderId, holderType: holderType}, {}, {});
  }

  getOneByAgentId(scheduleId: string, agentId: string) {
    const api = this.ScheduleAPI.actions.getOneByAgentId;
    return this.$$call(api, {}, {}, {agentId: agentId, scheduleId: scheduleId});
  }

  updateOneByAgentId(schedule, scheduleId, agentId, type, instanceDate) {
    const api = this.ScheduleAPI.actions.updateOneByAgentId;
    return this.$$call(api, {type: type, date: instanceDate}, schedule, {agentId: agentId, scheduleId: scheduleId});
  }

  updateScheduleExtraData(scheduleExtraDataPayloadList, zoneId) {
    const api = this.ScheduleAPI.actions.updateScheduleExtraData;
    return this.$$call(api, {zoneId: zoneId}, scheduleExtraDataPayloadList, {});
  }

  deleteOneByAgentId(scheduleId, agentId, type, instanceDate) {
    const api = this.ScheduleAPI.actions.deleteOneByAgentId;
    return this.$$call(api, {type: type, date: instanceDate}, {}, {id: agentId, scheduleId: scheduleId});
  }

  getByDepotId(depotId, from, to, programType) {
    const api = this.ScheduleAPI.actions.getByDepotId;
    return this.$$call(
      api,
      {from: from, to: to, programType: programType, zoneId: this.splitService.getTimeZone()},
      {},
      {id: depotId}
    );
  }

  createManyByDepotId(schedules, agentId) {
    const api = this.ScheduleAPI.actions.createManyByDepotId;
    return this.$$call(api, {}, schedules, {id: agentId});
  }

  updateOneByDepotId(schedule, scheduleId, agentId, type, instanceDate) {
    const api = this.ScheduleAPI.actions.updateOneByDepotId;
    return this.$$call(api, {type: type, date: instanceDate}, schedule, {id: agentId, scheduleId: scheduleId});
  }

  deleteOneByDepotId(scheduleId, agentId, type, instanceDate) {
    const api = this.ScheduleAPI.actions.deleteOneByDepotId;
    return this.$$call(api, {type: type, date: instanceDate}, {}, {id: agentId, scheduleId: scheduleId});
  }

  getAllTemplates() {
    const api = this.ScheduleTemplateAPI.actions.getAll;
    return this.$$callTemplates(api, {}, {}, {});
  }

  getTemplateById(templateId) {
    const api = this.ScheduleTemplateAPI.actions.getById;
    return this.$$callTemplates(api, {}, {}, {id: templateId});
  }

  createTemplate(template) {
    const api = this.ScheduleTemplateAPI.actions.createOne;
    return this.$$callTemplates(api, {}, template, {});
  }

  updateTemplate(template) {
    const api = this.ScheduleTemplateAPI.actions.updateOne;
    return this.$$callTemplates(api, {}, template, {id: template.id});
  }

  getTemplateAgents(templateId) {
    const api = this.ScheduleTemplateAPI.actions.getTemplateAgents;
    return this.$$callTemplates(api, {}, {}, {id: templateId});
  }

  getTemplateSchedule(templateId, from, to) {
    const api = this.ScheduleAPI.actions.getTemplateSchedule;
    return this.$$call(api, {from: from, to: to, zoneId: this.splitService.getTimeZone()}, {}, {id: templateId});
  }

  createTemplateSchedule(templateId, schedule) {
    const api = this.ScheduleAPI.actions.createTemplateSchedule;
    return this.$$call(api, {}, schedule, {id: templateId});
  }

  updateTemplateSchedule(templateId, scheduleId, schedule, type, instanceDate) {
    const api = this.ScheduleAPI.actions.updateTemplateSchedule;
    return this.$$call(api, {type: type, date: instanceDate}, schedule, {id: templateId, scheduleId: scheduleId});
  }

  deleteTemplate(templateId) {
    const api = this.ScheduleTemplateAPI.actions.deleteOne;
    return this.$$callTemplates(api, {}, {}, {id: templateId});
  }

  deleteTemplateSchedule(templateId, scheduleId, type, instanceDate) {
    const api = this.ScheduleAPI.actions.deleteTemplateSchedule;
    return this.$$call(api, {type: type, date: instanceDate}, {}, {id: templateId, scheduleId: scheduleId});
  }

  getScheduleTimeline(scheduleId, from, to) {
    const api = this.ScheduleAPI.actions.getScheduleTimeline;
    return this.$$call(api, {from: from, to: to}, {}, {scheduleId: scheduleId});
  }

  //branches:
  getByBranchId(from, to, agentId) {
    const api = this.ScheduleAPI.actions.getByBranchId;
    return this.$$call(api, {from: from, to: to, zoneId: this.splitService.getTimeZone()}, {}, {id: agentId});
  }

  createManyByBranchId(schedules, agentId) {
    const api = this.ScheduleAPI.actions.createManyByBranchId;
    return this.$$call(api, {}, schedules, {id: agentId});
  }

  updateOneByBranchId(schedule, scheduleId, agentId, type, instanceDate) {
    const api = this.ScheduleAPI.actions.updateOneByBranchId;
    return this.$$call(api, {type: type, date: instanceDate}, schedule, {id: agentId, scheduleId: scheduleId});
  }

  deleteOneByBranchId(scheduleId, agentId, type, instanceDate) {
    const api = this.ScheduleAPI.actions.deleteOneByBranchId;
    return this.$$call(api, {type: type, date: instanceDate}, {}, {id: agentId, scheduleId: scheduleId});
  }

  approveSchedule(payload: {id: string, from: string, to: string}[]) {
    const api = this.ScheduleAPI.actions.approveSchedule;
    return this.$$call(api, {}, payload, {});
  }

  rejectSchedule(ids: string[]) {
    const api = this.ScheduleAPI.actions.rejectSchedule;
    return this.$$call(api, {}, ids, {});
  }

  exportAgentSchedules(params) {
    const api = this.ScheduleAPI.actions.exportAgentSchedules;
    return this.$$call(api, params, {}, {});
  }

  // ******************************
  // Internal methods
  // ******************************

  $$call(_apiCall, _params, _data, _options, scheduleId?) {
    return this.$schedule(scheduleId).$call(_apiCall, _params, _data, _options);
  }

  $$callTemplates(_apiCall, _params, _data, _options, templateId?) {
    return this.$scheduleTemplate(templateId).$call(_apiCall, _params, _data, _options);
  }

}

angular
  .module('bringoz.admin.schedule')
  .service('scheduleRequestsService', ScheduleRequestsService);
